import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import _ from 'lodash';
import { selectInstitution, } from '../../../../../store/features/system';
import { updateInstitution } from '../../../../../store/features/system/action-creators';
import { getAngularService } from '../../../../core/helpers/get-angular-service';
import { showNotification } from '../../../../../store/features/notification/action-creators';
import { NotificationType } from '../../../../../store/features/notification/types';
import { ColorPicker } from './components/color-picker';
import { ImageUpload } from './components/image-upload';
import { PreviewModal } from './components/preview-modal';
import { ResetConfirmModal } from './components/reset-confirm-modal';
import { DEFAULT_THEME_VALUES } from './constants';
import './clinic-branding.scss';
const hexRegExp = /^#([\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/u;
const validateHex = (value) => {
    if (!value) {
        return true;
    }
    if (!value.includes('#')) {
        return false;
    }
    return hexRegExp.test(value);
};
const normalizeHex = (value) => {
    if (!value) {
        return '';
    }
    if (!value.includes('#')) {
        return `#${value}`;
    }
    return value;
};
// Helper function to check if institution can be styled (mimics Angular InstitutionMethods.canStyle)
const canStyleInstitution = (inst) => {
    if (inst === null ||
        inst === undefined ||
        inst.id === undefined ||
        inst.id === null) {
        return false;
    }
    return [
        'frontColor',
        'backgroundColor',
        'logoHorizontal',
        'logoVertical',
    ].every((key) => Boolean(inst[key]));
};
// Helper function to add canStyle method to institution object
const addCanStyleMethod = (inst) => {
    if (inst === null || inst === undefined) {
        return inst;
    }
    return {
        ...inst,
        canStyle: () => canStyleInstitution(inst),
    };
};
export const ClinicBranding = () => {
    const intl = useIntl();
    const formatMessage = intl.formatMessage.bind(intl);
    const dispatch = useDispatch();
    const currentInstitution = useSelector(selectInstitution);
    const [institution, setInstitution] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [savePromise, setSavePromise] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [originalSettings, setOriginalSettings] = useState({});
    const [resetField, setResetField] = useState(null);
    const saveButtonRef = useRef(null);
    const themeService = getAngularService('themeService');
    const notificationFactory = getAngularService('notificationFactory');
    const InstitutionService = getAngularService('Institution');
    const $interval = getAngularService('$interval');
    const $timeout = getAngularService('$timeout');
    const $rootScope = getAngularService('$rootScope');
    const institutionHelper = getAngularService('institutionHelper');
    useEffect(() => {
        const fetchInstitution = async () => {
            if (currentInstitution.id === 0) {
                return;
            }
            try {
                const data = await InstitutionService.findById({
                    id: currentInstitution.id,
                }).$promise;
                setInstitution(addCanStyleMethod(data));
                setOriginalSettings(_.pick(data, Object.keys(data).filter((key) => typeof data[key] === 'string')));
                setIsLoading(false);
            }
            catch {
                setIsLoading(false);
            }
        };
        void fetchInstitution();
    }, [currentInstitution.id, InstitutionService]);
    const formik = useFormik({
        initialValues: {
            frontColor: institution?.frontColor ?? '',
            backgroundColor: institution?.backgroundColor ?? '',
            contrastBack: institution?.contrastBack ?? '',
            sidebarTextColor: institution?.sidebarTextColor ?? '',
            welcomeScreenText: institution?.welcomeScreenText ?? '',
            behindLogoColor: institution?.behindLogoColor ?? '',
            logoVertical: institution?.logoVertical ?? '',
            logoHorizontal: institution?.logoHorizontal ?? '',
            favicon: institution?.favicon ?? '',
            backgroundImage: institution?.backgroundImage ?? '',
        },
        enableReinitialize: true,
        validate: (values) => {
            const errors = {};
            if (!values.frontColor) {
                errors.frontColor = formatMessage({
                    id: 'account.settings.enterprise.theme.required',
                });
            }
            else if (!validateHex(values.frontColor)) {
                errors.frontColor = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            if (!values.backgroundColor) {
                errors.backgroundColor = formatMessage({
                    id: 'account.settings.enterprise.theme.required',
                });
            }
            else if (!validateHex(values.backgroundColor)) {
                errors.backgroundColor = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            if (values.contrastBack && !validateHex(values.contrastBack)) {
                errors.contrastBack = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            if (values.sidebarTextColor && !validateHex(values.sidebarTextColor)) {
                errors.sidebarTextColor = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            if (values.welcomeScreenText && !validateHex(values.welcomeScreenText)) {
                errors.welcomeScreenText = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            if (values.behindLogoColor && !validateHex(values.behindLogoColor)) {
                errors.behindLogoColor = formatMessage({
                    id: 'account.settings.enterprise.theme.hex-warning',
                });
            }
            return errors;
        },
        onSubmit: (values) => {
            if (!institution || institution.id === 0) {
                return;
            }
            // Use Angular LoopBack SDK directly (same as admin theme page)
            const promise = InstitutionService.prototype$updateAttributes({ id: institution.id }, values)
                .$promise.then((result) => {
                const updatedWithMethods = addCanStyleMethod(result);
                setInstitution(updatedWithMethods);
                void formik.setFieldValue('logoVertical', result.logoVertical);
                void formik.setFieldValue('logoHorizontal', result.logoHorizontal);
                void formik.setFieldValue('favicon', result.favicon);
                void formik.setFieldValue('backgroundImage', result.backgroundImage);
                // Update Redux store with new institution data
                dispatch(updateInstitution({
                    logoHorizontal: result.logoHorizontal,
                    logoVertical: result.logoVertical,
                    frontColor: result.frontColor,
                    backgroundColor: result.backgroundColor,
                    contrastBack: result.contrastBack,
                    sidebarTextColor: result.sidebarTextColor,
                    welcomeScreenText: result.welcomeScreenText,
                    behindLogoColor: result.behindLogoColor,
                    favicon: result.favicon,
                    backgroundImage: result.backgroundImage,
                }));
                // Also update local reference for immediate use
                currentInstitution.logoHorizontal = result.logoHorizontal;
                currentInstitution.logoVertical = result.logoVertical;
                currentInstitution.frontColor = result.frontColor;
                currentInstitution.backgroundColor = result.backgroundColor;
                currentInstitution.contrastBack = result.contrastBack;
                currentInstitution.sidebarTextColor = result.sidebarTextColor;
                currentInstitution.welcomeScreenText = result.welcomeScreenText;
                currentInstitution.behindLogoColor = result.behindLogoColor;
                currentInstitution.favicon = result.favicon;
                currentInstitution.backgroundImage = result.backgroundImage;
                themeService.applyInstitutionTheme(updatedWithMethods);
                // Explicitly update favicon link element to ensure it refreshes immediately
                if (result.favicon !== null &&
                    result.favicon !== undefined &&
                    result.favicon !== '') {
                    const faviconLink = document.querySelector("link[rel*='icon']");
                    if (faviconLink) {
                        faviconLink.href = `${result.favicon}?${Date.now()}`;
                    }
                }
                // Clear institutionHelper cache to force fresh fetch on next access
                // This ensures sidebar logo gets updated data when it calls getCurrentInstitution()
                if (institutionHelper !== null &&
                    institutionHelper !== undefined &&
                    typeof institutionHelper.getCurrentInstitutionNoCache === 'function') {
                    // Force refresh the cache by calling getCurrentInstitutionNoCache
                    // This will clear the cache and fetch fresh data
                    const cachePromise = institutionHelper.getCurrentInstitutionNoCache();
                    // eslint-disable-next-line promise/no-nesting
                    void cachePromise.catch(() => {
                        // Ignore errors - cache will be refreshed on next access
                    });
                }
                // Emit event to update sidebar logo and other theme-dependent components
                $rootScope.$emit('institutionThemeUpdate');
                // Reset form dirty state
                for (const key of Object.keys(formik.values)) {
                    void formik.setFieldTouched(key, false);
                }
                return result;
            })
                .catch((error) => {
                dispatch(showNotification(formatMessage({
                    id: 'account.settings.clinic.branding.save-error',
                }), NotificationType.ERROR));
                throw error;
            });
            setSavePromise(promise);
        },
    });
    const handlePreview = useCallback(() => {
        if (!institution || !canStyleInstitution(institution)) {
            notificationFactory.showCommon('warning', 'no-preview');
            return;
        }
        const previewInstitution = addCanStyleMethod({
            ...institution,
            ...formik.values,
        });
        themeService.applyInstitutionTheme(previewInstitution);
        setIsPreviewOpen(true);
    }, [institution, formik.values, themeService, notificationFactory]);
    const handleClosePreview = useCallback(() => {
        setIsPreviewOpen(false);
        if (institution && Object.keys(originalSettings).length > 0) {
            themeService.applyInstitutionTheme(addCanStyleMethod(institution));
        }
    }, [institution, originalSettings, themeService]);
    // Replicate Angular d-progress-button-promise directive behavior
    useEffect(() => {
        if (!savePromise || !saveButtonRef.current) {
            return;
        }
        const element = saveButtonRef.current;
        const bar = element.querySelector('.progress-inner');
        if (!bar) {
            return;
        }
        let intervalId = null;
        let value = 0;
        const increment = (stat) => 6 * Math.exp(-5 * (stat / 100));
        const startProgress = () => {
            element.classList.add('progress-loading');
            element.setAttribute('disabled', 'disabled');
            intervalId = $interval(() => {
                value = value + increment(value);
                bar.style.width = `${value}%`;
            }, 250);
        };
        const resetBar = () => {
            bar.classList.add('notransition');
            bar.style.width = '0';
            $timeout(() => {
                bar.classList.remove('notransition');
            }, 250);
        };
        const finishState = (isSuccess) => {
            const finishClass = `state-${isSuccess ? 'success' : 'error'}`;
            element.classList.add(finishClass);
            $timeout(() => {
                element.classList.remove(finishClass);
                element.classList.remove('progress-loading');
                element.removeAttribute('disabled');
                setSavePromise(null);
            }, 2000);
        };
        const progressEnd = (isSuccess) => {
            if (intervalId !== null && intervalId !== undefined) {
                $interval.cancel(intervalId);
            }
            bar.style.width = '100%';
            $timeout(() => {
                resetBar();
                finishState(isSuccess);
            }, 500);
        };
        startProgress();
        void savePromise
            .then(() => {
            progressEnd(true);
            return undefined;
        })
            .catch(() => {
            progressEnd(false);
            return undefined;
        });
        // eslint-disable-next-line consistent-return
        return () => {
            if (intervalId !== null && intervalId !== undefined) {
                $interval.cancel(intervalId);
            }
        };
    }, [savePromise, $interval, $timeout]);
    const handleColorChange = useCallback((field, value) => {
        const normalized = normalizeHex(value);
        void formik.setFieldValue(field, normalized);
        void formik.setFieldTouched(field, true);
    }, [formik]);
    const handleColorBlur = useCallback((field) => {
        void formik.setFieldTouched(field, true);
    }, [formik]);
    const handleImageUpload = useCallback((imageType, url) => {
        switch (imageType) {
            case 'logoVertical': {
                void formik.setFieldValue('logoVertical', url);
                break;
            }
            case 'logoHorizontal': {
                void formik.setFieldValue('logoHorizontal', url);
                break;
            }
            case 'favicon': {
                void formik.setFieldValue('favicon', url);
                break;
            }
            case 'backgroundImage': {
                void formik.setFieldValue('backgroundImage', url);
                break;
            }
            // No default
        }
    }, [formik]);
    const handleResetClick = useCallback((fieldName) => {
        setResetField(fieldName);
    }, []);
    const handleResetConfirm = useCallback(() => {
        if (resetField === null) {
            return;
        }
        const defaultValue = DEFAULT_THEME_VALUES[resetField];
        void formik.setFieldValue(resetField, defaultValue);
        void formik.setFieldTouched(resetField, true);
        setResetField(null);
    }, [resetField, formik]);
    const handleResetCancel = useCallback(() => {
        setResetField(null);
    }, []);
    if (isLoading) {
        return <div className="clinic-branding__loading">Loading...</div>;
    }
    if (!institution) {
        return (<div className="clinic-branding__error">
        Failed to load institution data
      </div>);
    }
    const isDirty = formik.dirty;
    const hasErrors = Object.keys(formik.errors).length > 0;
    return (<div className="clinic-branding__wrapper">
      <header className="settings-wrap settings-header">
        <h2 className="header-title">
          <FormattedMessage id="account.settings.enterprise.theme.title"/>
        </h2>
        <p className="header-description">
          <FormattedMessage id="account.settings.enterprise.theme.description"/>{' '}
          <a href="https://help.doxy.me/en/articles/95904-branded-clinic-account-examples" target="_blank" rel="noopener noreferrer" className="help-link">
            <FormattedMessage id="account.settings.clinic.branding.help-link"/>
          </a>
        </p>
      </header>

      {isDirty && (<div className="save">
          <p className="text-center">
            <FormattedMessage id="account.settings.enterprise.theme.save-preview"/>
          </p>
        </div>)}

      <form className="settings-wrap label-pushed clearfix" onSubmit={formik.handleSubmit}>
        <div className="form-group">
          {formik.errors.frontColor && formik.touched.frontColor && (<span className="validation-invalid validation-invalid__none-top">
              {formik.errors.frontColor}
            </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.accent-color"/>
          </label>
          <ColorPicker value={formik.values.frontColor} onChange={(value) => handleColorChange('frontColor', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('frontColor')} showReset fieldName="frontColor" defaultValue={DEFAULT_THEME_VALUES.frontColor} accentColor={formik.values.frontColor} textInputId="accentColorInput" colorPickerId="accentColorPicker" resetButtonId="accentColorRevertBtn" placeholder={formatMessage({
            id: 'account.settings.enterprise.theme.required',
        })} required/>
        </div>

        <hr />

        <div className="form-group">
          {formik.errors.backgroundColor && formik.touched.backgroundColor && (<span className="validation-invalid validation-invalid__none-top">
              {formik.errors.backgroundColor}
            </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.background-color"/>
          </label>
          <ColorPicker value={formik.values.backgroundColor} onChange={(value) => handleColorChange('backgroundColor', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('backgroundColor')} showReset fieldName="backgroundColor" defaultValue={DEFAULT_THEME_VALUES.backgroundColor} accentColor={formik.values.frontColor} textInputId="backgroundColorInput" colorPickerId="backgroundColorPicker" resetButtonId="backgroundColorRevertBtn" placeholder={formatMessage({
            id: 'account.settings.enterprise.theme.required',
        })} required/>
        </div>

        <div className="form-group">
          {formik.errors.contrastBack && formik.touched.contrastBack && (<span className="validation-invalid validation-invalid__none-top">
              {formik.errors.contrastBack}
            </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.sidebar-selected"/>
          </label>
          <ColorPicker value={formik.values.contrastBack} onChange={(value) => handleColorChange('contrastBack', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('contrastBack')} showReset fieldName="contrastBack" defaultValue={DEFAULT_THEME_VALUES.contrastBack} accentColor={formik.values.frontColor} backgroundColor={formik.values.backgroundColor} showHelpIcon helpTooltipText={formatMessage({
            id: 'account.settings.clinic.branding.sidebar-selected-help',
        })} textInputId="contrastBackInput" colorPickerId="contrastBackPicker" resetButtonId="contrastBackRevertBtn"/>
        </div>

        <div className="form-group">
          {formik.errors.sidebarTextColor &&
            formik.touched.sidebarTextColor && (<span className="validation-invalid validation-invalid__none-top">
                {formik.errors.sidebarTextColor}
              </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.sidebar-text"/>
          </label>
          <ColorPicker value={formik.values.sidebarTextColor} onChange={(value) => handleColorChange('sidebarTextColor', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('sidebarTextColor')} showReset fieldName="sidebarTextColor" defaultValue={DEFAULT_THEME_VALUES.sidebarTextColor} accentColor={formik.values.frontColor} textInputId="sidebarTextColorInput" colorPickerId="sidebarTextColorPicker" resetButtonId="sidebarTextColorRevertBtn"/>
        </div>

        <div className="form-group">
          {formik.errors.welcomeScreenText &&
            formik.touched.welcomeScreenText && (<span className="validation-invalid validation-invalid__none-top">
                {formik.errors.welcomeScreenText}
              </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.landing-text"/>
          </label>
          <ColorPicker value={formik.values.welcomeScreenText} onChange={(value) => handleColorChange('welcomeScreenText', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('welcomeScreenText')} showReset fieldName="welcomeScreenText" defaultValue={DEFAULT_THEME_VALUES.welcomeScreenText} accentColor={formik.values.frontColor} frontColor={formik.values.frontColor} showHelpIcon helpTooltipText={formatMessage({
            id: 'account.settings.clinic.branding.landing-text-help',
        })} textInputId="welcomeScreenTextInput" colorPickerId="welcomeScreenTextPicker" resetButtonId="welcomeScreenTextRevertBtn"/>
        </div>

        <div className="form-group">
          {formik.errors.behindLogoColor && formik.touched.behindLogoColor && (<span className="validation-invalid validation-invalid__none-top">
              {formik.errors.behindLogoColor}
            </span>)}
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.logo-background"/>
          </label>
          <ColorPicker value={formik.values.behindLogoColor} onChange={(value) => handleColorChange('behindLogoColor', value)} onBlur={handleColorBlur} onReset={() => handleResetClick('behindLogoColor')} showReset fieldName="behindLogoColor" defaultValue={DEFAULT_THEME_VALUES.behindLogoColor} accentColor={formik.values.frontColor} textInputId="behindLogoColorInput" colorPickerId="behindLogoColorPicker" resetButtonId="behindLogoColorRevertBtn"/>
        </div>

        <div className="form-group">
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.landing-logo"/>
          </label>
          {!formik.values.logoVertical && (<p className="help-block">
              <FormattedMessage id="account.settings.enterprise.theme.required-logo"/>
            </p>)}
          <ImageUpload imageType="logoVertical" institutionId={institution.id} onUpload={handleImageUpload} onReset={() => handleResetClick('logoVertical')} showReset uploadButtonId="landingPageLogoUploadBtn" resetButtonId="landingPageLogoRevertBtn"/>
          {formik.values.logoVertical && (<div>
              <img className="space-top-md" src={formik.values.logoVertical} alt="Vertical logo" width="82"/>
            </div>)}
        </div>

        <div className="form-group">
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.sidebar-logo"/>
          </label>
          {!formik.values.logoHorizontal && (<p className="help-block">
              <FormattedMessage id="account.settings.enterprise.theme.required-logo"/>
            </p>)}
          <ImageUpload imageType="logoHorizontal" institutionId={institution.id} onUpload={handleImageUpload} onReset={() => handleResetClick('logoHorizontal')} showReset uploadButtonId="sidebarLogoUploadBtn" resetButtonId="sidebarLogoRevertBtn"/>
          {formik.values.logoHorizontal && (<div>
              <img className="space-top-md" src={formik.values.logoHorizontal} alt="Horizontal logo" width="142"/>
            </div>)}
        </div>

        <div className="form-group">
          <label>Favicon</label>
          <ImageUpload imageType="favicon" institutionId={institution.id} onUpload={handleImageUpload} onReset={() => handleResetClick('favicon')} showReset uploadButtonId="faviconUploadBtn" resetButtonId="faviconRevertBtn"/>
          {formik.values.favicon && (<div>
              <img className="space-top-md" src={formik.values.favicon} alt="Favicon" width="24"/>
            </div>)}
        </div>

        <div className="form-group">
          <label>
            <FormattedMessage id="account.settings.enterprise.theme.background-img"/>
          </label>
          <ImageUpload imageType="backgroundImage" institutionId={institution.id} onUpload={handleImageUpload} onReset={() => handleResetClick('backgroundImage')} showReset uploadButtonId="backgroundImageUploadBtn" resetButtonId="backgroundImageRevertBtn"/>
          {formik.values.backgroundImage && (<div>
              <img className="space-top-md space-bottom-md" src={formik.values.backgroundImage} alt="Background" width="340"/>
            </div>)}
        </div>

        <button type="button" className="btn btn-primary btn-block" onClick={handlePreview}>
          <FormattedMessage id="account.settings.enterprise.theme.preview-btn"/>
        </button>

        <button ref={saveButtonRef} type="submit" className="btn btn-primary progress-button btn-block" disabled={hasErrors}>
          <span className="progress">
            <span className="progress-inner"></span>
          </span>
          <span className="progress-content">
            <span className="progress-default">
              <FormattedMessage id="account.settings.enterprise.theme.save-btn"/>
            </span>
            <span className="progress-success">
              <i className="icon icon-ok"></i>
            </span>
            <span className="progress-error">
              <i className="icon icon-close"></i>
            </span>
          </span>
        </button>

        {hasErrors && (<p className="save-warning text-center">
            <FormattedMessage id="account.settings.enterprise.theme.save-warning"/>
          </p>)}
      </form>

      {isPreviewOpen && (<PreviewModal isOpen={isPreviewOpen} onClose={handleClosePreview} institution={institution}/>)}

      <ResetConfirmModal isOpen={resetField !== null} onClose={handleResetCancel} onConfirm={handleResetConfirm}/>
    </div>);
};
